import {
  auditLogActionTypeColor,
  auditLogRequestStatusColor,
  userRoleColor,
} from "@lib/helpers/ColorHelper";
import { Tag, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { ApiParams } from "@lib/types/Api";
import { MessageFormatElement } from "react-intl";
import { NavigateFunction } from "react-router-dom";
import TableIdButton from "@lib/components/MiniComponents/Buttons/TableIdButton";
import { shortenString, showCountryName } from "@lib/helpers/StringHelper";
import { ColumnConfig, generateColumns } from "@lib/helpers/TableHelper";
import { AuditLog } from "@lib/types/Monitoring";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { getRoute } from "@lib/helpers/RouteHelper";
import { ROUTES } from "@lib/constants";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";

type Props<T> = {
  apiData: T[];
  navigate?: NavigateFunction;
  params: ApiParams;
  messages: Record<string, string> | Record<string, MessageFormatElement[]>;
};

const getColumns = <T extends AuditLog>(props: Props<T>): ColumnsType<T> => {
  const columnsConfig: ColumnConfig<T>[] = [
    {
      title: "common.createdAt",
      dataIndex: "createdAt",
      width: "150px",
      render: (d) => (
        <Tooltip title={dayjs(d).format("DD/MM/YYYY HH:mm")}>
          {dayjs(d).fromNow()}
        </Tooltip>
      ),
    },

    {
      title: "common.managerName",
      dataIndex: "manager.firstName",
      width: "170px",
      render: (value, record) =>
        shortenString(
          `${record?.manager?.firstName} ${record?.manager?.lastName}`,
        ),
      filterType: "search",
    },

    {
      title: "common.managerEmail",
      dataIndex: "manager.email",
      width: "180px",
      render: (value, record) => (
        <TableIdButton
          text={`${shortenString(record?.manager?.email)}`}
          textToBeCopied={record?.manager?.email}
        />
      ),
      filterType: "search",
    },

    {
      title: "common.role",
      dataIndex: "manager.role",
      width: "150px",
      render: (value, record) => (
        <Tag color={userRoleColor(record?.manager?.role)}>
          {record?.manager?.role}
        </Tag>
      ),
      filterType: "dropdown",
    },

    {
      title: "common.country",
      dataIndex: "country",
      width: "110px",
      render: (value) => showCountryName(value),
      filterType: "dropdown",
    },

    {
      title: "common.actionType",
      dataIndex: "actionType",
      width: "130px",
      render: (value) => (
        <Tag color={auditLogActionTypeColor(value)}>{value}</Tag>
      ),
      filterType: "dropdown",
    },

    {
      title: "common.ipAddress",
      dataIndex: "ipAddress",
      width: "160px",
      render: (value) => (
        <TableIdButton
          text={`${shortenString(value)}`}
          textToBeCopied={value}
        />
      ),
      filterType: "search",
    },

    {
      title: "common.requestStatus",
      dataIndex: "reqStatus",
      width: "140px",
      render: (value) => (
        <Tag color={auditLogRequestStatusColor(value)}>{value}</Tag>
      ),
      filterType: "dropdown",
    },

    {
      title: "common.platform",
      dataIndex: "extraData.auditReq.headers.user-agent",
      width: "150px",
      render: (value, record) => (
        <TableIdButton
          text={`${shortenString(record?.extraData?.auditReq?.headers["user-agent"])}`}
          textToBeCopied={record?.extraData?.auditReq?.headers["user-agent"]}
        />
      ),
      filterType: "search",
    },

    {
      title: "common.actions",
      dataIndex: "actions",
      className: "order-table-action",
      fixed: "right",
      width: "90px",
      render: (_, record) => (
        <StyledFlex className="actions">
          <ActionButton
            type="detail"
            noLabel
            size="small"
            href={getRoute(ROUTES.MONITORING.AUDIT_LOGS.DETAIL, {
              auditLogId: record.id,
            })}
          />
        </StyledFlex>
      ),
    },
  ];

  return generateColumns(props, columnsConfig);
};

export default getColumns;
